import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridFooter, GridToolbar } from "@mui/x-data-grid";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  IconButton,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { spacing } from "@mui/system";
import axios from "axios";
import ViewCustomerInvoiceDialog from "../../components/ViewCustomerInvoiceDialog";
import moment from "moment";
import SelectWrapper from "../../components/SelectBoxDataGridCell";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DataGridExport from "../../components/DataGridExport";
import ConfirmDialog from "../../components/ConfirmDialog";
import ViewClaimDialog from "../../components/ViewClaimDialog";
import PreFinanceCodeLink from "../../components/PreFinanceCodeLink";
import PreFinanceDossierDetailDialog from "../../components/PreFinanceDossierDetailDialog";
import { usePreFinanceDossierDialog } from "../../hooks/usePreFinanceDossierDialog";
import { formatPreFinanceDisplayCode } from "../../utils/preFinanceCodeUtils";
import { getThemeTextColor } from "../../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";
import { GlobalContext } from "../../contexts/GlobalContext";
// import apiUtils from "../../utils/apiUtils";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const CustomerInvoices = (props) => {
  const theme = useTheme();
  const { globalData } = useContext(GlobalContext);
  const { hasPermission } = usePermissions();
  const canWriteInvoices = hasPermission(CAPABILITIES.invoicesWrite, "W");
  const canLinkPreFinance =
    canWriteInvoices || hasPermission(CAPABILITIES.prefinanceCreate, "X");

  // Get the current user ID from the context
  const getCurrentUserId = () => {
    if (!globalData?.userId) {
      console.error("Global context data is not available");
      return null;
    }
    return globalData.userId;
  };

  const [pageSize, setPageSize] = React.useState(15);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    customerInvoiceId: false,
  });
  const customerId = props.customerId;
  const customerNumber = props.customerNumber;
  var updatedData;

  const [loading, setLoading] = useState(false);

  const [supplierData, setSupplierData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/suppliers").then((response) => setSupplierData(response.data));
    }
    fetchData();
  }, []);

  const [countryData, setCountryData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/countries").then((response) => setCountryData(response.data));
    }
    fetchData();
  }, []);

  const [invoiceStateData, setInvoiceStateData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/invoiceStates").then((response) => setInvoiceStateData(response.data));
    }
    fetchData();
  }, []);

  const [invoiceLabelData, setInvoiceLabelData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/invoiceLabels").then((response) => setInvoiceLabelData(response.data));
    }
    fetchData();
  }, []);

  const [productData, setProductData] = useState([]);

  useEffect(() => {
    async function fetchData() {
      axios.get("/api/v1/productTypes").then((response) => setProductData(response.data));
    }
    fetchData();
  }, []);

  const [currencyData, setCurrencyData] = useState([]);

  useEffect(() => {
    axios.get("/api/v1/currencies").then((response) => setCurrencyData(response.data));
  }, []);

  const [customerPreFinanceData, setCustomerPreFinanceData] = useState([]);

  useEffect(() => {
    axios.get("/api/v1/preFinanceByEntityId/" + customerId).then((response) => setCustomerPreFinanceData(response.data));
  }, [customerId]);

  const [customerClaimsData, setCustomerClaimsData] = useState([]);

  useEffect(() => {
    axios.get("/api/v1/claimsByEntityId/" + customerId).then((response) => setCustomerClaimsData(response.data));
  }, [customerId]);

  // const [supplierData, setSupplierData] = useState([]);
  // const [currencyData, setCurrencyData] = useState([]);
  // const [productData, setProductData] = useState([]);
  // const [countryData, setCountryData] = useState([]);
  // const [customerPreFinanceData, setCustomerPreFinanceData] = useState([]);

  // useEffect(() => {
  //   // Fetch and set supplier data
  //   const fetchedSupplierData = apiUtils.useApiData("/api/v1/suppliers");
  //   setSupplierData(fetchedSupplierData);

  //   // Fetch and set currency data
  //   const fetchedCurrencyData = apiUtils.useApiData("/api/v1/currencies");
  //   setCurrencyData(fetchedCurrencyData);

  //   // Fetch and set product data
  //   const fetchedProductData = apiUtils.useApiData("/api/v1/productTypes");
  //   setProductData(fetchedProductData);

  //   // Fetch and set country data
  //   const fetchedCountryData = apiUtils.useApiData("/api/v1/countries");
  //   setCountryData(fetchedCountryData);

  //   // Fetch and set customer pre-finance data
  //   const fetchedCustomerPreFinanceData = apiUtils.useApiData(
  //     "/api/v1/preFinanceByEntityId/" + customerId
  //   );
  //   setCustomerPreFinanceData(fetchedCustomerPreFinanceData);
  // }, [customerId]);

  // console.table(customerPreFinanceData);

  const [viewClaimDialog, setViewClaimDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
  });

  const {
    selectedDossier,
    dialogContext,
    loadingPreFinanceId,
    error: preFinanceDialogError,
    setError: setPreFinanceDialogError,
    openDossierById,
    closeDossier,
    canFinalizePreFinance,
    canUnfinalizePreFinance,
  } = usePreFinanceDossierDialog();

  const columns = [
    {
      field: "customerInvoiceId",
      headerName: "id",
      flex: 0.5,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 0.48,
    },
    {
      field: "invoiceDate",
      headerName: "Invoice Date",
      flex: 0.35,
      valueFormatter: (params) => moment(params?.value).format("DD-MM-YYYY"),
    },
    {
      field: "supplierId",
      headerName: "Supplier",
      flex: 0.9,
      valueGetter: (params) => {
        if (supplierData.length > 0) {
          const supplier = supplierData.find((supplier) => supplier.supplierId === params.row.supplierId);
          return supplier ? supplier.supplierName : "";
        }
        return "";
      },
    },
    {
      field: "countryCode",
      headerName: "Country",
      flex: 0.25,

      valueGetter: (params) => {
        if (countryData.length > 0) {
          const country = countryData.find((country) => country.countryId === params.row.countryCode);
          return country ? country.countryCode : "";
        }
        return "";
      },
    },
    {
      field: "currencyId",
      headerName: "Currency",
      flex: 0.25,
      valueGetter: (params) => {
        if (currencyData.length > 0) {
          const currency = currencyData.find((cur) => cur.currencyId === params.row.currencyId);
          return currency ? currency.isoCode : "";
        }
        return "";
      },
    },
    {
      field: "invoiceVATAmountLocalCurrency",
      headerName: "VAT Amount",
      flex: 0.4,
      align: "right",
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: "15px",
          }}
        >
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
    },
    {
      field: "invoiceStateId",
      headerName: "Invoice State",
      flex: 0.33,
      valueGetter: (params) => {
        if (invoiceStateData.length > 0) {
          const invoiceState = invoiceStateData.find((invoiceState) => invoiceState.invoiceStateId === params.row.invoiceStateId);
          return invoiceState ? invoiceState.invoiceStateShortCode : "";
        }
        return "";
      },
    },
    {
      field: "claimState",
      headerName: "Claim State",
      flex: 0.33,
      renderCell: (params) => {
        // Ensure claimId is treated as a string and checked if it's not empty
        const claimId = String(params.row.claimId).trim();

        if (claimId) {
          // console.table(customerClaimsData);
          // console.log(claimId);
          const claim = customerClaimsData.find((c) => String(c.claimId) === claimId);
          // console.log(claim);

          if (claim) {
            let claimStateLabel = "";
            switch (claim.claimStateId) {
              case 4:
                claimStateLabel = "Pending";
                break;
              case 16:
                claimStateLabel = "Finished";
                break;
              default:
                claimStateLabel = ""; // Default case if no relevant stateId matches
            }

            if (claimStateLabel) {
              return (
                <Button
                  variant="text"
                  onClick={() => {
                    setViewClaimDialog({
                      isOpen: true,
                      title: "View Claim",
                      subTitle: "Details for Claim " + claimId,
                      params: params.row, // Pass the row data to the dialog
                      custInfo: [{ customerId: params.row.customerId, customerNumber: params.row.customerNumber }],
                    });
                  }}
                >
                  {claimStateLabel}
                </Button>
              );
            }
          }
        }
        return ""; // Return empty string if no claimId or claim not found
      },
    },
    {
      field: "claimCodeDisplay",
      headerName: "Claim Code",
      flex: 0.58,
      valueGetter: (params) => {
        const claimId = String(params.row.claimId ?? "").trim();
        if (!claimId) {
          return "";
        }
        const claim = customerClaimsData.find((c) => String(c.claimId) === claimId);
        return claim?.claimCode != null ? String(claim.claimCode) : "";
      },
      renderCell: (params) => {
        const claimCodeText = params.value;
        if (!claimCodeText) {
          return "";
        }
        const claimId = String(params.row.claimId ?? "").trim();
        return (
          <Button
            variant="text"
            onClick={() => {
              setViewClaimDialog({
                isOpen: true,
                title: "View Claim",
                subTitle: "Details for Claim " + claimId,
                params: params.row,
                custInfo: [{ customerId: params.row.customerId, customerNumber: params.row.customerNumber }],
              });
            }}
          >
            {claimCodeText}
          </Button>
        );
      },
    },
    {
      field: "preFinanceCode",
      headerName: "Pre-Finance Code",
      flex: 0.55,
      valueGetter: (params) => {
        if (!params.row.preFinanceId || params.row.preFinanceId <= 0) {
          return "";
        }

        const preFinance = customerPreFinanceData.find(
          (item) => item.preFinanceId === params.row.preFinanceId
        );

        return formatPreFinanceDisplayCode({
          preFinanceId: params.row.preFinanceId,
          preFinanceCode: preFinance?.preFinanceCode,
          isLegacy: preFinance?.isLegacy,
        });
      },
      renderCell: (params) => {
        const preFinance = customerPreFinanceData.find(
          (item) => item.preFinanceId === params.row.preFinanceId
        );

        return (
          <PreFinanceCodeLink
            preFinanceId={params.row.preFinanceId}
            preFinanceCode={preFinance?.preFinanceCode}
            isLegacy={preFinance?.isLegacy}
            onOpen={(preFinanceId) =>
              openDossierById(preFinanceId, { customerId, customerNumber })
            }
            isLoading={loadingPreFinanceId === params.row.preFinanceId}
          />
        );
      },
    },
    {
      headerName: "Pre-Fin Date",
      flex: 0.4,
      valueGetter: (params) => {
        const found = customerPreFinanceData.find((item) => item.preFinanceId === params.row.preFinanceId);
        return found ? moment(found.payoutDate).format("DD-MM-YYYY") : "";
      },
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: "15px",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "fileId",
      headerName: "PDF",
      flex: 0.2,
      renderCell: (params) =>
        params.value > 0 ? (
          <CheckCircleOutlineIcon style={{ color: "green", margin: "0 auto" }} />
        ) : (
          <CheckCircleOutlineIcon style={{ color: "red", margin: "0 auto" }} />
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.35,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              disabled={!canWriteInvoices}
              aria-label="Delete invoice"
              onClick={(e) => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Invoice",
                  subTitle: "Are you sure you want to delete invoice " + params.row.invoiceNumber + "?",
                  onConfirm: () => {
                    onDeleteConfirmed(e, params.row);
                  },
                });
              }}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                setViewCustomerInvoiceDialog({
                  isOpen: true,
                  downloadFile: true,
                  title: "Manage Invoice",
                  params: params.row,
                  custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                  productInfo: [
                    {
                      productCode: params.row.claimProductCode,
                      productSubCode: params.row.claimProductSubCode,
                    },
                  ],
                  subTitle: "Are you sure you want to manage " + params.row.contactName + "?",
                  onConfirm: () => {
                    onEditConfirmed(e, params.row);
                  },
                  handleFile: () => {
                    handleFile(updatedData);
                  },
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleFile = (updatedData) => {};

  const [tableData, setTableData] = useState([]);

  // console.table(tableData);

  const fetchCustomerInvoices = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/customerInvoicesByEntityIdWithProductCodes/" + customerId);
      setTableData(response.data);
    } catch (error) {
      console.error("Error fetching customer invoices by entity ID:", error);
    }
  }, [customerId]);

  useEffect(() => {
    let isCancelled = false;

    const fetchAsync = async () => {
      try {
        const response = await axios.get("/api/v1/customerInvoicesByEntityIdWithProductCodes/" + customerId);
        if (!isCancelled) {
          setTableData(response.data);
        }
      } catch (error) {
        console.error("Error fetching customer invoices by entity ID:", error);
      }
    };

    fetchAsync();

    return () => {
      isCancelled = true;
    };
  }, [fetchCustomerInvoices, customerId]);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {}, // Empty function for now, will be updated when needed
  });

  const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
    productInfo: [],
  });

  function onEditConfirmed(e, params) {
    window.location.reload(false);
    setViewCustomerInvoiceDialog({
      ...viewCustomerInvoiceDialog,
      isOpen: false,
    });
  }

  const handleEditCellChange = (params) => {
    const updatedRows = tableData.map((row) => (row.id === params.id ? { ...row, [params.field]: params.props.value } : row));
    setTableData(updatedRows);
  };

  const onDeleteConfirmed = async (e, params) => {
    try {
      await axios.delete("/api/v1/customerInvoices/" + params.customerInvoiceId);

      // Remove from table data
      const tableData2 = tableData.filter((invoice) => invoice.customerInvoiceId !== params.customerInvoiceId);
      setTableData(tableData2);

      // Refresh data to ensure consistency
      fetchCustomerInvoices();

      // Show success message
      setOpenAlert(true);
      setAlertSeverity("success");
      setAlertMessage("Invoice " + params.invoiceNumber + " deleted successfully!");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);

      setConfirmDialog({ ...confirmDialog, isOpen: false });
    } catch (error) {
      // Show error message
      setOpenAlert(true);
      setAlertSeverity("error");
      setAlertMessage(error.response?.data?.message || "Error deleting invoice");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);

      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };
  const [selectedCountry, setSelectedCountry] = useState("any");
  const [selectedYear, setSelectedYear] = useState("any");
  const [selectedStartMonth, setSelectedStartMonth] = useState("any");
  const [selectedEndMonth, setSelectedEndMonth] = useState("any");
  const [selectedState, setSelectedState] = useState("any");

  const getPreviewData = useCallback(async () => {
    setLoading(true);
    await axios.get("/api/v1/customerInvoicesByEntityIdWithProductCodes/" + customerId).then((response) => {
      const invoicesReturn = response.data;
      let invoicesFiltered = invoicesReturn;
      if (selectedCountry !== "any") {
        invoicesFiltered = invoicesFiltered.filter((inv) => inv.countryCode === selectedCountry);
      }
      if (selectedYear !== "any") {
        invoicesFiltered = invoicesFiltered.filter((inv) => {
          const invYear = new Date(inv.invoiceDate).getFullYear();
          return invYear === selectedYear;
        });
      }
      if (selectedYear !== "any" && selectedStartMonth !== "any" && selectedEndMonth !== "any") {
        invoicesFiltered = invoicesFiltered.filter((inv) => {
          const invYear = new Date(inv.invoiceDate).getFullYear();
          const invMonth = new Date(inv.invoiceDate).getMonth();
          const filterStartDate = new Date(selectedYear, selectedStartMonth - 1, 1);
          const filterEndDate = new Date(selectedYear, selectedEndMonth, 0);
          const invoiceDate = new Date(invYear, invMonth, 1);
          return invoiceDate >= filterStartDate && invoiceDate <= filterEndDate;
        });
      }
      if (selectedState !== "any") {
        invoicesFiltered = invoicesFiltered.filter((inv) => inv.invoiceStateId === selectedState);
      }
      console.log("invoicesFiltered", invoicesFiltered);
      setTableData(invoicesFiltered);
      setLoading(false);
    }, 2000);
  }, [customerId, selectedCountry, selectedYear, selectedStartMonth, selectedEndMonth, selectedState]);

  function generateArrayOfYears() {
    var max = new Date().getFullYear() + 1;
    var min = 2021;
    var years = [];
    for (var i = max; i >= min; i--) {
      years.push({ yearId: i, yearText: i });
    }

    return years;
  }

  let yearArr = generateArrayOfYears().reverse();

  const monthArr = [
    { monthId: 1, monthText: "Jan" },
    { monthId: 2, monthText: "Feb" },
    { monthId: 3, monthText: "Mar" },
    { monthId: 4, monthText: "Apr" },
    { monthId: 5, monthText: "May" },
    { monthId: 6, monthText: "Jun" },
    { monthId: 7, monthText: "Jul" },
    { monthId: 8, monthText: "Aug" },
    { monthId: 9, monthText: "Sep" },
    { monthId: 10, monthText: "Oct" },
    { monthId: 11, monthText: "Nov" },
    { monthId: 12, monthText: "Dec" },
  ];

  const handleCountryValue = (value, name) => {
    setSelectedCountry(value);
  };
  const handleYearValue = (value, name) => {
    setSelectedYear(value);
  };
  const handleStartMonthValue = (value, name) => {
    setSelectedStartMonth(value);
  };
  const handleEndMonthValue = (value, name) => {
    setSelectedEndMonth(value);
  };
  const handleStateValue = (value) => {
    setSelectedState(value);
  };

  const handleFilter = async () => {
    getPreviewData();
  };

  useEffect(() => {
    getPreviewData();
  }, [selectedCountry, selectedYear, getPreviewData]);

  const handleResetFilter = async () => {
    try {
      const response = await axios.get("/api/v1/customerInvoicesByEntityIdWithProductCodes/" + customerId);
      setTableData(response.data);
    } catch (error) {
      console.error("Error fetching customer invoices by entity ID:", error);
    }
  };

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedInvoiceCount, setSelectedInvoiceCount] = useState(0);
  const [selectedInvoiceTotalAmount, setSelectedInvoiceTotalAmount] = useState(0);
  const [selectedInvoiceTotalVATAmount, setSelectedInvoiceTotalVATAmount] = useState(0);
  const [isPreFinancedDisabled, setIsPreFinancedDisabled] = useState(false);
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
  const [isInvoiceDownloadInProgress, setIsInvoiceDownloadInProgress] = useState(false);
  const downloadMenuCloseTimeoutRef = useRef(null);
  const [entitySettings, setEntitySettings] = useState({
    allowPreFinance: false,
    blockedPreFinance: false,
  });

  // const [
  //   selectedInvoiceTotalVATAmountEUR,
  //   setSelectedInvoiceTotalVATAmountEUR,
  // ] = useState(0);

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
    let selectedRowsAmount = 0;
    let selectedRowsVATAmount = 0;
    let disablePreFinanced = false;

    newSelection.forEach((rowId) => {
      const selectedRow = tableData.find((row) => row.customerInvoiceId === rowId);
      if (selectedRow) {
        selectedRowsAmount += selectedRow.invoiceGrossAmount;
        selectedRowsVATAmount += selectedRow.invoiceVATAmountLocalCurrency;

        // Check if preFinancedId is greater than 0
        if (selectedRow.preFinanceId > 0) {
          disablePreFinanced = true;
        }
      }
    });

    setSelectedInvoiceCount(newSelection.length);
    setSelectedInvoiceTotalAmount(selectedRowsAmount);
    setSelectedInvoiceTotalVATAmount(selectedRowsVATAmount);

    // Update the button's disabled state
    setIsPreFinancedDisabled(disablePreFinanced);
  };

  const clearDownloadMenuCloseTimeout = useCallback(() => {
    if (!downloadMenuCloseTimeoutRef.current) {
      return;
    }

    clearTimeout(downloadMenuCloseTimeoutRef.current);
    downloadMenuCloseTimeoutRef.current = null;
  }, []);

  const handleOpenDownloadMenu = useCallback(
    (event) => {
      clearDownloadMenuCloseTimeout();
      setDownloadMenuAnchorEl(event.currentTarget);
    },
    [clearDownloadMenuCloseTimeout]
  );

  const handleCloseDownloadMenu = useCallback(() => {
    clearDownloadMenuCloseTimeout();
    setDownloadMenuAnchorEl(null);
  }, [clearDownloadMenuCloseTimeout]);

  const handleScheduleDownloadMenuClose = useCallback(() => {
    clearDownloadMenuCloseTimeout();
    downloadMenuCloseTimeoutRef.current = setTimeout(() => {
      setDownloadMenuAnchorEl(null);
    }, 150);
  }, [clearDownloadMenuCloseTimeout]);

  const getInvoiceDownloadErrorMessage = useCallback(async (error) => {
    const errorData = error?.response?.data;

    if (errorData instanceof Blob) {
      try {
        const errorText = await errorData.text();
        const parsedError = JSON.parse(errorText);
        return parsedError?.detail || parsedError?.message || errorText || "Error downloading invoices";
      } catch (parseError) {
        try {
          const fallbackText = await errorData.text();
          return fallbackText || "Error downloading invoices";
        } catch (blobReadError) {
          return "Error downloading invoices";
        }
      }
    }

    return error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Error downloading invoices";
  }, []);

  const handleDownloadSelectedInvoices = useCallback(
    async (mergeIntoSinglePdf) => {
      handleCloseDownloadMenu();

      if (selectedRows.length <= 0) {
        setOpenAlert(true);
        setAlertSeverity("error");
        setAlertMessage("No invoices selected");
        setTimeout(() => {
          setOpenAlert(false);
        }, 5000);
        return;
      }

      setIsInvoiceDownloadInProgress(true);

      try {
        const response = await axios.post(
          "/api/v1/customerInvoices/customInvoiceDownload",
          {
            customerInvoiceIds: selectedRows,
            mergeIntoSinglePdf,
          },
          {
            responseType: "blob",
          }
        );

        const downloadUrl = URL.createObjectURL(response.data);
        const downloadLink = document.createElement("a");
        const customerIdentifier = customerNumber || customerId || "customer";

        downloadLink.href = downloadUrl;
        downloadLink.download = mergeIntoSinglePdf
          ? `${customerIdentifier}-merged-invoices.zip`
          : `${customerIdentifier}-invoices.zip`;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadUrl);

        setOpenAlert(true);
        setAlertSeverity("success");
        setAlertMessage(mergeIntoSinglePdf ? "Merged invoice ZIP download started" : "Invoice ZIP download started");
      } catch (error) {
        const errorMessage = await getInvoiceDownloadErrorMessage(error);

        setOpenAlert(true);
        setAlertSeverity("error");
        setAlertMessage(errorMessage);
      } finally {
        setTimeout(() => {
          setOpenAlert(false);
        }, 5000);
        setIsInvoiceDownloadInProgress(false);
      }
    },
    [customerId, customerNumber, getInvoiceDownloadErrorMessage, handleCloseDownloadMenu, selectedRows]
  );

  useEffect(() => {
    return () => {
      clearDownloadMenuCloseTimeout();
    };
  }, [clearDownloadMenuCloseTimeout]);

  const handleSetInvoiceState = (invoiceStateId, invoiceStateLabel) => {
    if (selectedRows.length <= 0) {
      setOpenAlert(true);
      setAlertSeverity("error");
      setAlertMessage("No invoices selected");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);
      return;
    }

    // Show the custom ConfirmDialog
    setConfirmDialog({
      isOpen: true,
      title: "Confirm Set State",
      subTitle: `Are you sure you want to set selected invoices to state '${invoiceStateLabel}'?`,
      onConfirm: () => {
        let rowsToBeUpdated = [];
        selectedRows.forEach((rowId) => {
          const selectedRow = {};

          selectedRow.customerInvoiceId = rowId;
          selectedRow.invoiceStateId = invoiceStateId;

          if (selectedRow) {
            rowsToBeUpdated.push(selectedRow);
          }
        });
        const jsonRowsToBeUpdated = JSON.stringify(rowsToBeUpdated);
        axios
          .put("/api/v1/customerInvoices/updateInvoiceStateId", jsonRowsToBeUpdated, {
            headers: {
              "Content-Type": "application/json",
            },
          })
          .then((response) => {
            if (response.status !== 204) {
              // Replace 200 with the actual status code your API returns upon successful update
              throw new Error("Failed to update invoice states");
            }
            // console.table(response.data);
            getPreviewData();
            setSelectedRows(0);
            setOpenAlert(true);
            setAlertSeverity("success");
            setAlertMessage("Invoice states are updated!");
            setTimeout(() => {
              setOpenAlert(false);
            }, 5000);
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          })
          .catch((error) => {
            setOpenAlert(true);
            setAlertSeverity("error");
            setAlertMessage(error.message);
            setTimeout(() => {
              setOpenAlert(false);
            }, 5000);
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          });
      },
      onCancel: () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handleSetStateToRfc = () => {
    handleSetInvoiceState(3, "RFC");
  };

  const handleSetStateToTnc = () => {
    handleSetInvoiceState(2, "TNC");
  };

  const handlePreFinanced = () => {
    if (selectedRows.length <= 0) {
      setOpenAlert(true);
      setAlertSeverity("error");
      setAlertMessage("No invoices selected");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);
      return;
    }

    // Check if pre-finance is allowed for this customer
    if (!entitySettings.allowPreFinance || entitySettings.blockedPreFinance) {
      setOpenAlert(true);
      setAlertSeverity("error");
      setAlertMessage("Pre-finance is not allowed for this customer");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);
      return;
    }

    // Show the custom ConfirmDialog
    setConfirmDialog({
      isOpen: true,
      title: "Confirm Pre-Finance",
      subTitle: "Are you sure you want to set selected invoices to pre-financed?",
      onConfirm: () => {
        // Pre-finance action
        let newPreFinance = {
          entityId: customerId,
          creationDate: moment().format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
          payoutAmount: selectedInvoiceTotalVATAmount,
          payedOut: 0,
        };
        axios
          .post("/api/v1/initialCreatePreFinance", newPreFinance)
          .then((response) => {
            if (response.status !== 201) {
              // Replace 201 with the actual status code your API returns upon successful creation
              throw new Error("Failed to create preFinance");
            }
            // console.log(response);
            // Extract ID from the response
            const returnedId = response.data.preFinanceId;

            let rowsToBePreFinanced = [];
            selectedRows.forEach((rowId) => {
              const selectedRow = {};

              selectedRow.customerInvoiceId = rowId;
              selectedRow.preFinanceId = returnedId;

              if (selectedRow) {
                rowsToBePreFinanced.push(selectedRow);
              }
            });
            const jsonRowsToBePreFinanced = JSON.stringify(rowsToBePreFinanced);

            // Now use this ID in your PUT request
            return axios.put("/api/v1/customerInvoices/updateInvoicePreFinance", jsonRowsToBePreFinanced, {
              headers: {
                "Content-Type": "application/json",
              },
            });
          })
          .then((response) => {
            if (response.status !== 204) {
              // Replace 200 with the actual status code your API returns upon successful update
              throw new Error("Failed to update invoicePreFinance");
            }
            // console.table(response.data);
            getPreviewData();
            setSelectedRows(0);
            setOpenAlert(true);
            setAlertSeverity("success");
            setAlertMessage("Pre-finance settings are saved!");
            setTimeout(() => {
              setOpenAlert(false);
            }, 5000);
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          })
          .catch((error) => {
            setOpenAlert(true);
            setAlertSeverity("error");
            setAlertMessage(error.message);
            setTimeout(() => {
              setOpenAlert(false);
            }, 5000);
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          });
      },
    });
  };

  const BulkEditFooter = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #ccc",
          paddingTop: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Total Selected Invoices:</div>
            <div style={{ marginBottom: 5 }}>{selectedInvoiceCount}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Total Net Amount:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedInvoiceTotalAmount.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Total VAT Amount:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedInvoiceTotalVATAmount.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          {/* <div
            style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}
          >
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>
              Total VAT Amount EUR:
            </div>
            <div style={{ marginBottom: 5 }}>
              {selectedInvoiceTotalVATAmountEUR.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div> */}
        </div>
        <div>
          <div>
            <GridFooter
              style={{ borderTop: "none" }}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[15, 50, 100]}
            />
          </div>
        </div>
      </div>
    );
  };

  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");

  // Add sorting state management to preserve sorting when data refreshes
  const [sortModel, setSortModel] = useState([{ field: "invoiceDate", sort: "desc" }]);

  useEffect(() => {
    if (!preFinanceDialogError) {
      return;
    }

    setAlertSeverity("error");
    setAlertMessage(preFinanceDialogError);
    setOpenAlert(true);
    setPreFinanceDialogError("");
  }, [preFinanceDialogError, setPreFinanceDialogError]);

  // const showConfirmationDialog = () => {
  //   return window.confirm(
  //     "Are you sure you want to set selected invoices to pre-financed?"
  //   );
  // };

  useEffect(() => {
    const fetchEntitySettings = async () => {
      try {
        const response = await axios.get(`/api/v1/entitySettings/${customerId}`);
        setEntitySettings({
          allowPreFinance: response.data.allowPreFinance || false,
          blockedPreFinance: response.data.blockedPreFinance || false,
        });
      } catch (error) {
        console.error("Error fetching entity settings:", error);
        // If 404, create default settings
        if (error.response?.status === 404) {
          try {
            const defaultSettings = {
              entityId: customerId,
              allowPreFinance: false,
              blockedPreFinance: false,
              blockedPreFinanceReason: "",
              modificationUser: getCurrentUserId(),
            };
            await axios.post("/api/v1/entitySettings", defaultSettings);
            setEntitySettings({
              allowPreFinance: false,
              blockedPreFinance: false,
            });
          } catch (createError) {
            console.error("Error creating default entity settings:", createError);
          }
        }
      }
    };
    fetchEntitySettings();
  }, [customerId]);

  return (
    <React.Fragment>
      <ViewClaimDialog
        viewClaimDialog={viewClaimDialog}
        setViewClaimDialog={setViewClaimDialog}
        // refreshTable={fetchClaimsByEntityId}
      />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
      <PreFinanceDossierDetailDialog
        dossier={selectedDossier}
        open={Boolean(selectedDossier)}
        onClose={closeDossier}
        customerId={dialogContext.customerId ?? customerId}
        customerNumber={dialogContext.customerNumber ?? customerNumber}
        canFinalizePreFinance={canFinalizePreFinance}
        canUnfinalizePreFinance={canUnfinalizePreFinance}
      />

      <Snackbar
        open={openAlert}
        autoHideDuration={5000}
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenAlert(false)} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <Helmet title="Customer Invoices" />

      <Divider my={6} />
      <Grid item xs={12} md={6}></Grid>
      <Card mb={0}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 0,
          }}
        ></CardContent>
        <Grid container spacing={0} mt={0}>
          {/* <Grid container xs={12} sx={{ mb: 5 }}> */}
          <Grid container xs={12} spacing={5} sx={{ pr: 2 }}>
            <Grid item xs={2}>
              <SelectWrapper
                name="countryId"
                labelColor="#f64435"
                label="Country"
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(countryData).split('"countryId":').join('"key":')))
                      .split('"countryCode":')
                      .join('"value":')
                  ),
                ]}
                value={selectedCountry}
                handleChange={handleCountryValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="year"
                label="Year"
                // value={year}
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(yearArr).split('"yearId":').join('"key":')))
                      .split('"yearText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedYear}
                handleChange={handleYearValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="startMonth"
                label="Start month"
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedStartMonth}
                handleChange={handleStartMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="endMonth"
                label="End month"
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedEndMonth}
                handleChange={handleEndMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="invoiceStateId"
                label="Invoice State"
                options={[
                  { key: "any", value: "Any" },
                  ...invoiceStateData.map((state) => ({
                    key: state.invoiceStateId,
                    value: state.invoiceStateShortCode,
                  })),
                ]}
                value={selectedState}
                handleChange={handleStateValue}
              />
            </Grid>
            <Grid item xs={2}>
              <Button onClick={handleFilter} sx={{ mt: 4, pl: 6, pr: 6 }} variant="contained">
                Filter
              </Button>
              <Button onClick={handleResetFilter} sx={{ mt: 4, ml: 4, pl: 6, pr: 6 }} variant="outlined">
                Reset
              </Button>
            </Grid>
          </Grid>
          {viewCustomerInvoiceDialog.isOpen && (
            <ViewCustomerInvoiceDialog
              key={viewCustomerInvoiceDialog.params?.customerInvoiceId || Date.now()}
              viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
              setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
              countryArr={countryData}
              supplierArr={supplierData}
              currencyArr={currencyData}
              productTypeArr={productData}
              invoiceStateArr={invoiceStateData}
              invoiceLabelArr={invoiceLabelData}
              refreshGrid={getPreviewData}
            />
          )}
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <DataGridExport tableData={tableData} columns={columns} />
              <Box
                onMouseEnter={handleOpenDownloadMenu}
                onMouseLeave={handleScheduleDownloadMenuClose}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <IconButton color="primary" aria-label="download selected invoices">
                  {isInvoiceDownloadInProgress ? <CircularProgress size={20} /> : <DownloadIcon />}
                </IconButton>
              </Box>
              <Popper
                anchorEl={downloadMenuAnchorEl}
                open={Boolean(downloadMenuAnchorEl)}
                placement="bottom-start"
                modifiers={[
                  {
                    name: "offset",
                    options: {
                      offset: [0, 8],
                    },
                  },
                ]}
              >
                <Paper onMouseEnter={clearDownloadMenuCloseTimeout} onMouseLeave={handleScheduleDownloadMenuClose}>
                  <MenuList autoFocusItem={false} aria-label="download invoice options">
                    <MenuItem
                      onClick={() => handleDownloadSelectedInvoices(false)}
                      disabled={selectedRows.length <= 0 || isInvoiceDownloadInProgress}
                    >
                      Download as ZIP
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleDownloadSelectedInvoices(true)}
                      disabled={selectedRows.length <= 0 || isInvoiceDownloadInProgress}
                    >
                      Download merged ZIP
                    </MenuItem>
                  </MenuList>
                </Paper>
              </Popper>
            </div>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "600px",
                }}
              >
                <Typography sx={{ mr: 6, fontSize: 20, ...getThemeTextColor(theme) }}>Loading</Typography>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                // isRowSelectable={(params) =>
                //   params.row.preFinanceId <= 0 ||
                //   params.row.preFinanceId == null
                // }
                checkboxSelection
                disableSelectionOnClick={true}
                idProperty="id"
                editMode="cell"
                getRowId={(row) => row.customerInvoiceId}
                onEditCellChange={handleEditCellChange}
                selectionModel={selectedRows}
                onSelectionModelChange={handleSelectionChange}
                components={{ Toolbar: GridToolbar, Footer: BulkEditFooter }}
                density="compact"
                componentsProps={{
                  toolbar: {
                    csvOptions: { disableToolbarButton: false },
                    printOptions: { disableToolbarButton: false },
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 250 },
                  },
                }}
                rowIndexColumn
                experimentalFeatures={{ newEditingApi: true }}
                rows={tableData}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                pagination
                paginationMode="client"
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[15, 50, 100]}
                sx={{
                  height: 677,
                  width: "100%",
                  borderRadius: 1,
                  backgroundColor: "background.paper",
                  boxShadow: 2,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                  marginTop: 0,
                }}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "invoiceDate", sort: "desc" }],
                  },
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: 5,
                marginTop: 10,
                width: "100%",
                gap: 24,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handlePreFinanced}
                disabled={isPreFinancedDisabled || !canLinkPreFinance}
              >
                Set selected to pre-financed
              </Button>
              <Button variant="contained" color="warning" onClick={handleSetStateToTnc} disabled={!canWriteInvoices}>
                Set Invoice State to TNC
              </Button>
              <Button variant="contained" color="success" onClick={handleSetStateToRfc} disabled={!canWriteInvoices}>
                Set Invoice State to RFC
              </Button>
            </div>
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default CustomerInvoices;
